import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SignOutButton, useUser } from '@clerk/clerk-react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

// project import
import Transitions from 'components/@extended/Transitions';
import useConfig from 'hooks/useConfig';

// assets
import AimOutlined from '@ant-design/icons/AimOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import BulbOutlined from '@ant-design/icons/BulbOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import ProfileOutlined from '@ant-design/icons/ProfileOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';

const AVATAR_GRADIENT = 'linear-gradient(135deg,#c9d3e0,#aab6c6)';

// ==============================|| HEADER CONTENT - PROFILE (flat user menu) ||============================== //

export default function Profile({ compact = false, onCustomize }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { mode, onChangeMode } = useConfig();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const displayName = user ? user.fullName || user.username || user.primaryEmailAddress?.emailAddress || 'Trader' : 'Trader';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const avatarSrc = user?.imageUrl;

  const go = (path) => () => {
    setOpen(false);
    navigate(path);
  };

  const handleThemeToggle = (event) => {
    const nextMode = event.target.checked ? 'dark' : 'light';
    document.body.classList.toggle('dark-mode', nextMode === 'dark');
    onChangeMode(nextMode);
  };

  const items = [
    { key: 'view', icon: ProfileOutlined, label: 'View profile', onClick: go('/view-profile'), active: location.pathname === '/view-profile' && !location.search.includes('trader-info') },
    { key: 'trader', icon: AimOutlined, label: 'Trader info', onClick: go('/view-profile?tab=trader-info'), active: location.pathname === '/view-profile' && location.search.includes('trader-info') },
    onCustomize ? { key: 'customize', icon: AppstoreOutlined, label: 'Customize dashboard', onClick: () => { setOpen(false); onCustomize(); } } : null,
    { key: 'billing', icon: BankOutlined, label: 'Billing & plan', onClick: go('/account-settings') },
    { key: 'settings', icon: SettingOutlined, label: 'Settings', onClick: go('/account-settings'), active: location.pathname === '/account-settings' },
    { key: 'support', icon: MessageOutlined, label: 'Support', onClick: go('/contact-us') }
  ].filter(Boolean);

  return (
    <Box sx={{ flexShrink: 0 }}>
      <ButtonBase
        aria-label="open profile menu"
        ref={anchorRef}
        aria-haspopup="true"
        onClick={handleToggle}
        sx={{ borderRadius: '50%', '&:focus-visible': { outline: '2px solid #01A8F8', outlineOffset: 2 } }}
      >
        <Avatar
          alt={displayName}
          src={avatarSrc}
          sx={{
            width: compact ? 38 : 36,
            height: compact ? 38 : 36,
            background: AVATAR_GRADIENT,
            border: '2px solid',
            borderColor: open ? '#01A8F8' : 'transparent',
            transition: 'border-color 150ms ease'
          }}
        />
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ width: 264, borderRadius: '16px', overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: theme.customShadows?.z1 || '0 24px 50px -18px rgba(28,38,58,0.32)' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* identity header */}
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.25, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Avatar alt={displayName} src={avatarSrc} sx={{ width: 40, height: 40, background: AVATAR_GRADIENT }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: 'capitalize' }} color="text.primary" noWrap>
                        {displayName}
                      </Typography>
                      <Typography sx={{ fontSize: 12 }} color="text.secondary" noWrap>
                        {email}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* items */}
                  <Stack sx={{ p: 1 }}>
                    {items.map(({ key, icon: Icon, label, onClick, active }) => (
                      <Box
                        key={key}
                        component="button"
                        type="button"
                        onClick={onClick}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.35,
                          width: '100%',
                          px: 1.5,
                          py: 1.25,
                          border: 0,
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          bgcolor: active ? '#E6F7FF' : 'transparent',
                          color: active ? '#0088C7' : 'text.primary',
                          '&:hover': { bgcolor: active ? '#E6F7FF' : 'action.hover' }
                        }}
                      >
                        <Icon style={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: 'inherit' }}>{label}</Typography>
                      </Box>
                    ))}
                  </Stack>

                  {/* dark mode switch */}
                  <Box sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.35, width: '100%', px: 1.5, py: 0.5, borderRadius: '10px', color: 'text.primary' }}>
                      <BulbOutlined style={{ fontSize: 16 }} />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>Dark mode</Typography>
                      <Switch size="small" checked={mode === 'dark'} onChange={handleThemeToggle} inputProps={{ 'aria-label': 'Toggle dark mode' }} />
                    </Box>
                  </Box>

                  {/* log out */}
                  <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <SignOutButton asChild redirectUrl="/login">
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpen(false)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.35,
                          width: '100%',
                          px: 1.5,
                          py: 1.25,
                          border: 0,
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          bgcolor: 'transparent',
                          color: '#cf4040',
                          '&:hover': { bgcolor: 'rgba(207,64,64,0.08)' }
                        }}
                      >
                        <LogoutOutlined style={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'inherit' }}>Log out</Typography>
                      </Box>
                    </SignOutButton>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

Profile.propTypes = { compact: PropTypes.bool, onCustomize: PropTypes.func };
